<template>
  <div v-if="alerts.length > 0" class="mt-2 flex items-center gap-2">
    <span v-if="!hideLabel" class="text-sm font-medium text-gray-500">Alerts:</span>
    <div class="flex items-center gap-1.5 flex-wrap">
      <Tag
        v-for="alert in alerts"
        :key="alert.id"
        :label="alert.alert_name"
        :clickable="true"
        @click="openAlertModal(alert)"
      >
        <template #prefix>
          <FontAwesomeIcon
            :icon="getStatusIcon(alert.status)"
            class="text-xs shrink-0"
            :class="getIconColorClass(alert.status)"
          />
        </template>
      </Tag>
    </div>
  </div>
  <div v-else-if="loading" class="flex items-center gap-2 mt-2">
    <span v-if="!hideLabel" class="text-sm font-medium text-gray-500">Alerts:</span>
    <FontAwesomeIcon :icon="['fas', 'spinner']" spin class="text-gray-400 text-xs" />
  </div>

  <!-- Alert Detail Modal -->
  <div
    v-if="showAlertModal"
    class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
    @click="closeAlertModal"
  >
    <div
      class="bg-white rounded-lg max-w-4xl max-h-[70vh] overflow-auto w-full"
      @click.stop
    >
      <div class="flex justify-between items-center p-4 border-b border-gray-200">
        <h2 class="text-xl font-semibold">Alert Details</h2>
        <button
          class="text-gray-500 hover:text-gray-700 p-2"
          @click="closeAlertModal"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>
      <div class="p-0">
        <ComposableAlertDetail
          v-if="selectedAlertId"
          :alert-instance-id="selectedAlertId"
          @updated="fetchAlerts"
        />
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import ComposableAlertDetail from '@/components/InsightsNew/ComposableAlertDetail.vue';
import Tag from '@/components/widgetcomponents/library/Tag.vue';

export default {
  name: 'AlertBadge',
  components: {
    FontAwesomeIcon,
    ComposableAlertDetail,
    Tag,
  },
  props: {
    entityId: {
      type: [String, Number],
      required: true
    },
    hideLabel: {
      type: Boolean,
      default: false
    },
  },
  emits: ['count'],
  data() {
    return {
      alerts: [],
      loading: false,
      showAlertModal: false,
      selectedAlertId: null,
    };
  },
  watch: {
    entityId: {
      immediate: true,
      handler(newEntityId) {
        if (newEntityId) {
          this.fetchAlerts();
        }
      }
    }
  },
  methods: {
    async fetchAlerts() {
      if (!this.entityId) return;
      this.loading = true;
      try {
        const response = await axios.get('api/impact_analytics/composable-alert-instances/', {
          params: { entity_id: this.entityId, status: 'active' }
        });
        this.alerts = response.data?.results || response.data || [];
      } catch (error) {
        console.error('Error fetching alerts:', error);
        this.alerts = [];
      } finally {
        this.loading = false;
        this.$emit('count', this.alerts.length);
      }
    },
    
    getStatusIcon(status) {
      switch (status?.toLowerCase()) {
        case 'active':
          return ['fas', 'exclamation-triangle'];
        case 'acknowledged':
          return ['fas', 'eye'];
        case 'resolved':
          return ['fas', 'check'];
        default:
          return ['fas', 'question'];
      }
    },
    
    getIconColorClass(status) {
      switch (status?.toLowerCase()) {
        case 'active':
          return 'text-red-500';
        case 'acknowledged':
          return 'text-amber-500';
        case 'resolved':
          return 'text-emerald-500';
        default:
          return 'text-gray-500';
      }
    },

    openAlertModal(alert) {
      this.selectedAlertId = alert.id;
      this.showAlertModal = true;
    },

    closeAlertModal() {
      this.showAlertModal = false;
      this.selectedAlertId = null;
    },
  }
};
</script>
